// NavRail — MD3 NavigationRail для desktop (≥600px).
// Вертикальная панель с иконками и подписями. Активный item подсвечивается.
import { Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, Divider, Stack } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ContactsIcon from '@mui/icons-material/Contacts';
import BusinessIcon from '@mui/icons-material/Business';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import EventIcon from '@mui/icons-material/Event';
// V14 W9: «Отчёты» — аналитика (пункт навигации §3.6).
import BarChartIcon from '@mui/icons-material/BarChart';
// V15 W7: «Почта» — настройки/отправка писем (пункт навигации §3.8).
import MailIcon from '@mui/icons-material/Mail';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Logo } from '@/components/Logo';

interface NavItem {
  label: string;
  path: string;
  icon: JSX.Element;
}

const navItems: NavItem[] = [
  { label: 'Дашборд', path: '/dashboard', icon: <DashboardIcon /> },
  { label: 'Задачи', path: '/tasks', icon: <AssignmentIcon /> },
  { label: 'Сделки', path: '/crm/deals', icon: <TrendingUpIcon /> },
  { label: 'Контакты', path: '/crm/contacts', icon: <ContactsIcon /> },
  { label: 'Компании', path: '/crm/companies', icon: <BusinessIcon /> },
  // V12 U-34-E: лиды — между Компании и Календарь (постановка R62).
  { label: 'Лиды', path: '/crm/leads', icon: <PersonSearchIcon /> },
  // V12 U-35-C: «Платежи» — рядом с «Лиды» (постановка R66; фиксация: после Лиды, перед Календарь).
  { label: 'Платежи', path: '/payments', icon: <ReceiptLongIcon /> },
  // V14 W6: «Счета» — рядом с «Платежи» (финансовый контур; ТЗ §3.8 «пункт виден в навигации»).
  { label: 'Счета', path: '/invoices', icon: <RequestQuoteIcon /> },
  { label: 'Календарь', path: '/calendar', icon: <EventIcon /> },
  // V14 W9: «Отчёты» — после «Календарь» (позиция в ТЗ не зафиксирована; расширение, не переписывание).
  { label: 'Отчёты', path: '/reports', icon: <BarChartIcon /> },
  // V15 W7: «Почта» — после «Отчёты» (доступ к аккаунтам/шаблонам/истории, ТЗ §3.8).
  { label: 'Почта', path: '/settings/email', icon: <MailIcon /> },
];

export function NavRail(): JSX.Element {
  const location = useLocation();

  return (
    <Box
      component="nav"
      aria-label="primary navigation"
      sx={{
        width: 240,
        flexShrink: 0,
        borderRight: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        height: '100vh',
        position: 'sticky',
        top: 0,
        display: { xs: 'none', sm: 'block' },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Logo size={28} />
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Упряжка
          </Typography>
        </Stack>
      </Box>
      <Divider />
      <List dense>
        {navItems.map((item) => {
          const isActive = location.pathname.startsWith(item.path);
          return (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                component={RouterLink}
                to={item.path}
                selected={isActive}
                data-testid={`nav-${item.path}`}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
}
